import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import './FeedbackManagement.css';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import feedbackAdminAPI from '../../services/feedbackAdminApi';

const FeedbackManagement = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    score_range: '', // low: 1-2星, medium: 3星, high: 4-5星
    page: 1,
    page_size: 20
  });
  const [totalCount, setTotalCount] = useState(0);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [stats, setStats] = useState({
    total_feedbacks: 0,
    avg_score: 0,
    high_score_count: 0,
    low_score_count: 0,
    medium_score_count: 0
  });

  // 计算饼图数据 - 基于统计数据
  const getPieChartData = () => {
    return [
      { name: '好评 (4-5星)', value: stats.high_score_count, color: '#48bb78' },
      { name: '一般 (3星)', value: stats.medium_score_count, color: '#ed8936' },
      { name: '差评 (1-2星)', value: stats.low_score_count, color: '#f56565' }
    ];
  };

  const pieData = useMemo(() => getPieChartData(), [stats]);

  // 饼图自定义工具提示
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <TooltipContainer>
          <TooltipText>{payload[0].name}</TooltipText>
          <TooltipValue>{payload[0].value} 条</TooltipValue>
        </TooltipContainer>
      );
    }
    return null;
  };

  useEffect(() => {
    loadFeedbacks();
    loadStats();
  }, [filters]);

  const loadFeedbacks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await feedbackAdminAPI.getFeedbacks(
        filters.page,
        filters.page_size,
        filters.score_range || null
      );
      
      setFeedbacks(result.feedbacks || []);
      setTotalCount(result.pagination?.total || 0);
      
    } catch (err) {
      console.error('Error loading feedbacks:', err);
      setError(err.response?.data?.detail || err.message || '加载反馈失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await feedbackAdminAPI.getFeedbackStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error loading feedback stats:', err);
      // 统计加载失败不影响主功能，使用默认值
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // 重置页码
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleViewDetail = async (feedback) => {
    try {
      // 如果反馈数据不完整，从API获取详情
      if (!feedback.user_email || !feedback.user_phone) {
        const detail = await feedbackAdminAPI.getFeedbackDetail(feedback.id);
        setSelectedFeedback(detail);
      } else {
    setSelectedFeedback(feedback);
      }
    setShowDetailModal(true);
    } catch (err) {
      console.error('Error loading feedback detail:', err);
      // 即使获取详情失败，也显示基本信息
      setSelectedFeedback(feedback);
      setShowDetailModal(true);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreLabel = (score) => {
    if (score >= 4) return '好评';
    if (score === 3) return '一般';
    if (score <= 2) return '差评';
    return '未知';
  };

  const getScoreColor = (score) => {
    if (score >= 4) return '#48bb78'; // 绿色 - 好评
    if (score === 3) return '#ed8936'; // 橙色 - 一般
    if (score <= 2) return '#f56565'; // 红色 - 差评
    return '#718096'; // 灰色 - 未知
  };

  const renderStars = (score) => {
    return '★'.repeat(score) + '☆'.repeat(5 - score);
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingText>加载中...</LoadingText>
        </LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>反馈管理</PageTitle>
      </PageHeader>

      {/* 统计卡片 */}
      <StatsSection>
        <StatCard>
          <StatValue>{stats.total_feedbacks}</StatValue>
          <StatLabel>总反馈</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.avg_score.toFixed(1)}</StatValue>
          <StatLabel>平均评分</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.high_score_count}</StatValue>
          <StatLabel>好评(4-5星)</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.low_score_count}</StatValue>
          <StatLabel>差评(1-2星)</StatLabel>
        </StatCard>
      </StatsSection>

      {/* 评分分布饼图 */}
      <ChartSection>
        <ChartTitle>评分分布分析</ChartTitle>
        <ChartContainer>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="bottom" 
                height={36}
                formatter={(value, entry) => {
                  const total = pieData.reduce((sum, item) => sum + item.value, 0);
                  const percent = total > 0 ? ((entry.payload.value / total) * 100).toFixed(0) : 0;
                  return `${value}: ${percent}%`;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>
      </ChartSection>

      {/* 筛选和操作 */}
      <ControlSection>
        <FiltersRow>
          <FilterSelect 
            value={filters.score_range} 
            onChange={(e) => handleFilterChange('score_range', e.target.value)}
          >
            <option value="">全部评分</option>
            <option value="high">好评(4-5星)</option>
            <option value="medium">一般(3星)</option>
            <option value="low">差评(1-2星)</option>
          </FilterSelect>
        </FiltersRow>
      </ControlSection>

      {/* 反馈列表 */}
      {error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : (
        <FeedbackTable>
          <TableHeader>
            <TableRow>
              <TableHeaderCell width="60px">序号</TableHeaderCell>
              <TableHeaderCell width="100px">评分</TableHeaderCell>
              <TableHeaderCell width="500px">反馈内容</TableHeaderCell>
              <TableHeaderCell width="150px">用户</TableHeaderCell>
              <TableHeaderCell width="150px">创建时间</TableHeaderCell>
              <TableHeaderCell width="100px">操作</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {feedbacks.map((feedback, index) => (
              <TableRow key={feedback.id}>
                <TableCell>
                  {((filters.page - 1) * filters.page_size) + index + 1}
                </TableCell>
                <TableCell>
                  <ScoreBadge score={feedback.score}>
                    <StarDisplay>{renderStars(feedback.score)}</StarDisplay>
                    <ScoreLabel>{getScoreLabel(feedback.score)}</ScoreLabel>
                  </ScoreBadge>
                </TableCell>
                <TableCell>
                  <FeedbackContent>
                    <FeedbackComment>{feedback.comment}</FeedbackComment>
                  </FeedbackContent>
                </TableCell>
                <TableCell>
                  <UserInfo>
                    <UserName>{feedback.user_nickname}</UserName>
                    <UserEmail>{feedback.user_email}</UserEmail>
                  </UserInfo>
                </TableCell>
                <TableCell>
                  <TimeInfo>{formatDate(feedback.created_at)}</TimeInfo>
                </TableCell>
                <TableCell>
                  <ActionButtons>
                    <ActionButton 
                      onClick={() => handleViewDetail(feedback)}
                      style={{ backgroundColor: '#4299e1' }}
                    >
                      查看
                    </ActionButton>
                  </ActionButtons>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </FeedbackTable>
      )}

      {/* 分页 */}
      {!loading && feedbacks.length > 0 && (
        <PaginationSection>
          <PaginationButtons>
            <PaginationButton 
              onClick={() => handlePageChange(filters.page - 1)}
              disabled={filters.page === 1}
            >
              上一页
            </PaginationButton>
            <PaginationInfo>
              第 {filters.page} 页 / 共 {Math.ceil(totalCount / filters.page_size)} 页
            </PaginationInfo>
            <PaginationButton 
              onClick={() => handlePageChange(filters.page + 1)}
              disabled={filters.page >= Math.ceil(totalCount / filters.page_size)}
            >
              下一页
            </PaginationButton>
          </PaginationButtons>
        </PaginationSection>
      )}

      {/* 详情查看弹框 */}
      {showDetailModal && (
        <ModalOverlay onClick={() => setShowDetailModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <ModalHeader>
              <ModalTitle>反馈详情</ModalTitle>
              <CloseButton onClick={() => setShowDetailModal(false)}>×</CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <DetailSection>
                <DetailRow>
                  <DetailLabel>用户信息</DetailLabel>
                  <DetailValue>
                    <UserName>{selectedFeedback?.user_nickname || '未知用户'}</UserName>
                    {selectedFeedback?.user_email && (
                      <UserEmail>{selectedFeedback.user_email}</UserEmail>
                    )}
                    {selectedFeedback?.user_phone && (
                      <UserEmail>手机号: {selectedFeedback.user_phone}</UserEmail>
                    )}
                    {selectedFeedback?.user_id_str && (
                      <UserEmail>用户ID: {selectedFeedback.user_id_str}</UserEmail>
                    )}
                    {selectedFeedback?.user_point !== undefined && (
                      <UserEmail>积分: {selectedFeedback.user_point}</UserEmail>
                    )}
                    {selectedFeedback?.user_level && (
                      <UserEmail>等级: {selectedFeedback.user_level}</UserEmail>
                    )}
                  </DetailValue>
                </DetailRow>
                
                <DetailRow>
                  <DetailLabel>评分</DetailLabel>
                  <DetailValue>
                    <ScoreDisplay>
                      <StarDisplay>{renderStars(selectedFeedback?.score)}</StarDisplay>
                      <ScoreText>{selectedFeedback?.score}/5 分 - {getScoreLabel(selectedFeedback?.score)}</ScoreText>
                    </ScoreDisplay>
                  </DetailValue>
                </DetailRow>
                
                <DetailRow>
                  <DetailLabel>反馈内容</DetailLabel>
                  <DetailValue>
                    <FeedbackDetailContent>{selectedFeedback?.comment}</FeedbackDetailContent>
                  </DetailValue>
                </DetailRow>
                
                <DetailRow>
                  <DetailLabel>创建时间</DetailLabel>
                  <DetailValue>{formatDate(selectedFeedback?.created_at)}</DetailValue>
                </DetailRow>
                
                <DetailRow>
                  <DetailLabel>更新时间</DetailLabel>
                  <DetailValue>{formatDate(selectedFeedback?.updated_at)}</DetailValue>
                </DetailRow>
              </DetailSection>
            </ModalBody>
            
            <ModalFooter>
              <CancelButton onClick={() => setShowDetailModal(false)}>
                关闭
              </CancelButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default FeedbackManagement;

// 样式组件
const PageContainer = styled.div`
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  border: 2px solid ${props => props.highlight ? '#f56565' : '#e2e8f0'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.highlight ? '#f56565' : '#2d3748'};
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #718096;
`;

const ControlSection = styled.div`
  background: transparent;
  padding: 20px 0;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 16px;
`;

const FilterSelect = styled.select`
  width: 160px;
  padding: 10px 36px 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  color: #2d3748;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%234a5568' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  
  &:hover {
    border-color: #cbd5e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const FeedbackTable = styled.table`
  width: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.thead`
  background: #f7fafc;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e2e8f0;
  background: white;
  transition: background-color 0.2s ease;
  
  &:nth-child(even) {
    background-color: #fcfdfe;
  }
  
  &:hover {
    background-color: #f7fafc;
  }
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  width: ${props => props.width || 'auto'};
  background: #f7fafc;
`;

const TableBody = styled.tbody``;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #4a5568;
`;

const TypeBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background: ${props => {
    const colors = {
      suggestion: '#4299e1',
      complaint: '#f56565',
      praise: '#48bb78',
      bug_report: '#ed8936'
    };
    return colors[props.type] || '#718096';
  }};
`;

const FeedbackTitle = styled.div`
  font-weight: 500;
  color: #2d3748;
`;

const UserInfo = styled.div`
  font-weight: 500;
`;

const TimeInfo = styled.div`
  font-size: 12px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

const PaginationSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding: 20px;
`;

const PaginationInfo = styled.div`
  color: #4a5568;
  font-size: 14px;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  background: #e2e8f0;
  color: #2d3748;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #cbd5e0;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 弹框样式
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    color: #334155;
  }
`;

const ModalBody = styled.div`
  padding: 0 24px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #f1f5f9;
`;

const FeedbackInfo = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const FeedbackInfoTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
`;

const FeedbackInfoContent = styled.div`
  font-size: 14px;
  color: #4a5568;
  line-height: 1.6;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  color: #374151;
  background: white;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: white;
  color: #64748b;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #374151;
  }
`;

const SubmitButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const LoadingText = styled.div`
  color: #718096;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  background: #fff5f5;
  color: #c53030;
  padding: 16px 20px;
  border-radius: 8px;
  border: 1px solid #feb2b2;
  font-size: 14px;
  margin-bottom: 20px;
`;

// 新增的样式组件
const ScoreBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const StarDisplay = styled.div`
  color: #fbbf24;
  font-size: 16px;
  font-weight: bold;
`;

const ScoreLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${props => {
    const colors = {
      '好评': '#48bb78',
      '一般': '#ed8936', 
      '差评': '#f56565'
    };
    return colors[props.children] || '#718096';
  }};
`;

const FeedbackContent = styled.div`
  width: 100%;
  word-wrap: break-word;
  word-break: break-all;
`;

const FeedbackComment = styled.div`
  font-size: 14px;
  color: #4a5568;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const UserName = styled.div`
  font-weight: 500;
  color: #2d3748;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #718096;
`;

const ScoreDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ScoreText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
`;

// 详情弹框样式
const DetailSection = styled.div`
  margin-bottom: 24px;
`;

const DetailRow = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: flex-start;
`;

const DetailLabel = styled.div`
  width: 120px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  flex-shrink: 0;
`;

const DetailValue = styled.div`
  flex: 1;
  font-size: 14px;
  color: #4a5568;
`;

const FeedbackDetailContent = styled.div`
  font-size: 14px;
  color: #4a5568;
  line-height: 1.6;
  padding: 12px;
  background: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

// 饼图相关样式组件
const ChartSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 20px 0;
  text-align: left;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
`;

const TooltipContainer = styled.div`
  background: rgba(255, 255, 255, 0.96);
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const TooltipText = styled.div`
  color: #718096;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 4px;
`;

const TooltipValue = styled.div`
  color: #2d3748;
  font-size: 14px;
  font-weight: 700;
`;
